import { Cart } from "@insite/client-framework/Services/CartService";
import { CartLineModel, PaymentMethodDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import parseCurrencyDisplay from "@tSurya/Common/Utilities/parseCurrencyDisplay";
import { BRAND_PROPERTY_KEYS, normalizeSourceBrand } from "@tSurya/Common/Utilities/websiteUtils";
import Constant from "@tSurya/Constants/Constant";

/**
 * MGBW-only checkout Payment Breakdown logic (SR-1544).
 *
 * Mirrors the backend split-payment segmentation (Extensions/Helpers/BrandSegmentation.cs
 * and Extensions/Cart/UpdateCartHandler/ProcessCreditCardTransactions.cs):
 * - MG+BW is prepaid by credit card at checkout (its own payment entity), unless the
 *   bill-to customer is flagged as having MG+BW terms (customer custom property
 *   "MGBWTermCode" = "Y", synced from AX by the CustomerRefresh job). Those customers have
 *   terms with the MG+BW legal entity too, so the entire order is terms-eligible
 *   ("terms-for-all", business decision 2026-07-14: terms for MGBW implies terms for all
 *   brands).
 * - SURYA + GLOBAL VIEWS collapse into a single terms-eligible entity.
 *
 * Per-brand tax / freight / white glove amounts are read from the cart properties the
 * backend persists at checkout. Subtotals are computed client-side from the cart lines,
 * parsing the display value because the numeric extendedUnitNetPrice is 0 for configured
 * MGBW products.
 */

export type PaymentBreakdownScenario = "allCreditCard" | "mgbwPrepaySplit" | "termsEligible";

export type PaymentSplitChoice = "prepayAll" | "prepayMgbwOnly" | "payAllOnTerms";

export interface BrandGroupAmounts {
    subtotal: number;
    tax: number;
    freight: number;
    whiteGlove: number;
    rollPadService: number;
    shippingDiscount: number;
    total: number;
}

export interface PaymentBreakdownModel {
    scenario: PaymentBreakdownScenario;
    hasMgbw: boolean;
    hasSuryaGv: boolean;
    hasSurya: boolean;
    hasGlobalViews: boolean;
    mgbw: BrandGroupAmounts;
    suryaGv: BrandGroupAmounts;
    grandTotal: number;
    availableChoices: PaymentSplitChoice[];
    defaultChoice: PaymentSplitChoice;
}

export interface SplitTotals {
    chargedToday: number;
    onTerms: number;
}

/**
 * A "terms" (on-account / NET 30) payment method. It is detected by capability rather
 * than by name because the method name varies by environment (e.g. "NET 30 DAYS",
 * "AR42"): a terms method is not a credit card, eCheck, saved profile, or PayPal.
 * On MGBW it is removed from the dropdown and offered via the Payment Breakdown split
 * options instead.
 */
export const isTermsPaymentMethod = (
    method: Pick<PaymentMethodDto, "isCreditCard" | "isECheck" | "isPaymentProfile" | "name"> | null | undefined,
): boolean =>
    !!method &&
    !method.isCreditCard &&
    !method.isECheck &&
    !method.isPaymentProfile &&
    method.name !== "PayPal";

/**
 * Whether the account can actually pay by card. The backend strips every credit card and saved
 * profile method for certain terms codes (Extensions/Cart/GetCartHandler/DisableCCForPaymentTerms.cs),
 * which makes any split option that charges today unusable. Carts containing MGBW keep their methods
 * (MGBW is prepaid by card) and are flagged with `creditCardDisabledByTerms` instead.
 *
 * An unknown method list (order confirmation and print views, where paymentOptions is not
 * returned) counts as available so historical orders keep rendering the choice they were placed with.
 */
export const isCreditCardAvailable = (cart: Cart | undefined): boolean => {
    const paymentMethods = cart?.paymentOptions?.paymentMethods;
    if (!paymentMethods || paymentMethods.length === 0) {
        return true;
    }

    return paymentMethods.some(method => method.isCreditCard || method.isPaymentProfile);
};

/**
 * Cart property set by DisableCCForPaymentTerms when the customer's terms code disables credit card
 * but the cart contains MGBW, so the card methods had to stay available for the MGBW prepay charge.
 * The leading segment is camelCased by the API serializer.
 */
export const CREDIT_CARD_DISABLED_BY_TERMS_PROPERTY = "creditCardDisabledByTerms";

export const isCreditCardDisabledByTerms = (cart: Cart | undefined): boolean =>
    cart?.properties?.[CREDIT_CARD_DISABLED_BY_TERMS_PROPERTY]?.toLowerCase() === "true";

/**
 * The only "MGBWTermCode" value that grants MG+BW terms. AX sends it as an opt-in flag, so any
 * other value (including "N" or a stale term code) must keep the MGBW prepay requirement.
 */
export const MGBW_TERMS_ENABLED_VALUE = "Y";

/**
 * Whether the bill-to customer has payment terms with the MG+BW legal entity, detected by
 * the "MGBWTermCode" customer custom property (synced from AX by the CustomerRefresh job
 * and exposed on billTo.properties). The key lookup is case-insensitive because the exact
 * casing depends on the integration field map configuration.
 */
export const hasMgbwTermsProperty = (properties: { [key: string]: string } | null | undefined): boolean => {
    if (!properties) {
        return false;
    }
    const key = Object.keys(properties).find(propertyKey => propertyKey.toLowerCase() === "mgbwtermcode");
    return !!key && properties[key]?.trim().toUpperCase() === MGBW_TERMS_ENABLED_VALUE;
};

// The numeric extendedUnitNetPrice is 0 for configured MGBW products, so the displayed
// value is the source of truth; fall back to unit price * quantity when it is unavailable.
const getLineSubtotal = (line: CartLineModel): number => {
    const pricing = line.pricing;
    if (!pricing) {
        return 0;
    }
    const extended = parseCurrencyDisplay(pricing.extendedUnitNetPriceDisplay);
    if (extended > 0) {
        return extended;
    }
    return parseCurrencyDisplay(pricing.unitNetPriceDisplay) * (line.qtyOrdered ?? 0);
};

const isMgbwLine = (line: CartLineModel): boolean => {
    const normalized = normalizeSourceBrand(line.properties?.sourceBrand as string | undefined);
    return normalized === "mg+bw" || normalized === "mgbw" || normalized === "mitchellgold+bobwilliams";
};

// Within the terms-eligible group (everything that is not MGBW), a line is Global Views when its
// source brand reads as "Global Views" (spaces/hyphens stripped); otherwise it is treated as
// Surya. Used only to label the collapsed Surya + Global Views group with the brands actually in
// the cart - the segmentation still collapses them into one payment entity.
const isGlobalViewsLine = (line: CartLineModel): boolean => {
    const normalized = normalizeSourceBrand(line.properties?.sourceBrand as string | undefined).replace(
        /[^a-z0-9]/g,
        "",
    );
    return normalized.includes("globalview");
};

// Rug/Roll Pad service is a Surya-side add-on charge ($5 per qty on lines flagged
// isRollpadService), persisted on the backend as Cart.OtherCharges and included in the order
// grand total. It is attributed to the line's own payment group so the split reconciles.
const getLineRollPadService = (line: CartLineModel): number =>
    line.properties?.isRollpadService === "true"
        ? (line.qtyOrdered ?? 0) * Constant.rollServiceUnitPrice
        : 0;

const getNumericProperty = (cart: Cart, key: string): number => {
    const raw = cart.properties?.[key];
    if (!raw) {
        return 0;
    }
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : 0;
};

const roundCurrency = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export const getShippingPromotionTotal = (promotions: PromotionModel[] | undefined): number =>
    roundCurrency(
        (promotions ?? [])
            .filter(
                promotion =>
                    promotion.amount > 0 &&
                    (promotion.promotionResultType === "AmountOffShipping" ||
                        promotion.promotionResultType === "PercentOffShipping"),
            )
            .reduce((sum, promotion) => sum + promotion.amount, 0),
    );

const allocateShippingDiscount = (
    shippingDiscount: number,
    mgbwGrossFreight: number,
    suryaGvGrossFreight: number,
): { mgbw: number; suryaGv: number } => {
    const safeMgbwGrossFreight = Math.max(mgbwGrossFreight, 0);
    const safeSuryaGvGrossFreight = Math.max(suryaGvGrossFreight, 0);
    const totalGrossFreight = roundCurrency(safeMgbwGrossFreight + safeSuryaGvGrossFreight);
    const applicableDiscount = roundCurrency(Math.min(Math.max(shippingDiscount, 0), totalGrossFreight));

    if (applicableDiscount <= 0 || totalGrossFreight <= 0) {
        return { mgbw: 0, suryaGv: 0 };
    }

    const minimumMgbwDiscount = Math.max(applicableDiscount - safeSuryaGvGrossFreight, 0);
    const mgbwDiscount = roundCurrency(
        Math.min(
            Math.max(applicableDiscount * (safeMgbwGrossFreight / totalGrossFreight), minimumMgbwDiscount),
            safeMgbwGrossFreight,
        ),
    );
    return {
        mgbw: mgbwDiscount,
        suryaGv: roundCurrency(applicableDiscount - mgbwDiscount),
    };
};

const buildGroup = (
    subtotal: number,
    tax: number,
    freight: number,
    whiteGlove: number,
    rollPadService: number,
    shippingDiscount = 0,
): BrandGroupAmounts => {
    const roundedSubtotal = roundCurrency(subtotal);
    const roundedTax = roundCurrency(tax);
    const roundedFreight = roundCurrency(freight);
    const roundedWhiteGlove = roundCurrency(whiteGlove);
    const roundedRollPadService = roundCurrency(rollPadService);
    const roundedShippingDiscount = roundCurrency(shippingDiscount);
    return {
        subtotal: roundedSubtotal,
        tax: roundedTax,
        freight: roundedFreight,
        whiteGlove: roundedWhiteGlove,
        rollPadService: roundedRollPadService,
        shippingDiscount: roundedShippingDiscount,
        total: roundCurrency(
            roundedSubtotal +
                roundedTax +
                roundedFreight +
                roundedWhiteGlove +
                roundedRollPadService -
                roundedShippingDiscount,
        ),
    };
};

export const combineGroups = (a: BrandGroupAmounts, b: BrandGroupAmounts): BrandGroupAmounts =>
    buildGroup(
        a.subtotal + b.subtotal,
        a.tax + b.tax,
        a.freight + b.freight,
        a.whiteGlove + b.whiteGlove,
        a.rollPadService + b.rollPadService,
        a.shippingDiscount + b.shippingDiscount,
    );

/**
 * Builds the Payment Breakdown model from the cart and the user's terms eligibility.
 * Pure - no side effects, safe to call inside useMemo.
 *
 * @param userHasTerms The account exposes a terms payment method (detected by capability).
 * @param userHasMgbwTerms The bill-to is flagged for MG+BW terms (see hasMgbwTermsProperty):
 * the MG+BW portion is terms-eligible too, so the whole order can go on terms with no
 * split. Only honored when userHasTerms is also true - placing an order on terms requires
 * an actual terms payment method on the account.
 */
export const getPaymentBreakdown = (
    cart: Cart | undefined,
    userHasTerms: boolean,
    userHasMgbwTerms = false,
    shippingPromotionTotal = 0,
): PaymentBreakdownModel => {
    const lines = cart?.cartLines ?? [];
    const mgbwLines = lines.filter(isMgbwLine);
    const suryaGvLines = lines.filter(line => !isMgbwLine(line));

    const mgbwSubtotal = mgbwLines.reduce((sum, line) => sum + getLineSubtotal(line), 0);
    const suryaGvSubtotal = suryaGvLines.reduce((sum, line) => sum + getLineSubtotal(line), 0);

    const mgbwRollPadService = mgbwLines.reduce((sum, line) => sum + getLineRollPadService(line), 0);
    const suryaGvRollPadService = suryaGvLines.reduce((sum, line) => sum + getLineRollPadService(line), 0);

    // Per-brand tax / freight / white glove come from the cart properties the backend writes
    // during split segmentation (BrandTax_*, BrandFreight_*, BrandWhiteGlove_*). Those are only
    // populated at order submit, so at review-page display they are usually empty. When they are
    // missing we fall back to the always-accurate cart-level totals and allocate them across
    // brands by subtotal share so the combined figures reconcile with the order summary.
    //
    // Base freight (BrandFreight_*) excludes white glove so the breakdown can show White Glove on
    // its own line, mirroring the order summary. Tax collapses SURYA + GLOBAL VIEWS into SURYA.
    const totalShipping = roundCurrency(cart?.shippingAndHandling ?? 0);
    const totalTax = roundCurrency(cart?.totalTax ?? 0);

    const propMgbwFreight = cart ? getNumericProperty(cart, BRAND_PROPERTY_KEYS.freight.mgbw) : 0;
    const propSuryaGvFreight = cart
        ? getNumericProperty(cart, BRAND_PROPERTY_KEYS.freight.surya) +
          getNumericProperty(cart, BRAND_PROPERTY_KEYS.freight.globalViews)
        : 0;
    const propMgbwWhiteGlove = cart ? getNumericProperty(cart, BRAND_PROPERTY_KEYS.whiteGlove.mgbw) : 0;
    const propSuryaGvWhiteGlove = cart
        ? getNumericProperty(cart, BRAND_PROPERTY_KEYS.whiteGlove.surya) +
          getNumericProperty(cart, BRAND_PROPERTY_KEYS.whiteGlove.globalViews)
        : 0;
    const propMgbwTax = cart ? getNumericProperty(cart, BRAND_PROPERTY_KEYS.tax.mgbw) : 0;
    const propSuryaGvTax = cart ? getNumericProperty(cart, BRAND_PROPERTY_KEYS.tax.surya) : 0;

    // cart.shippingAndHandling already includes white glove; separate it so the base-freight
    // fallback allocation matches the summary (base shipping vs a distinct white glove line).
    const totalWhiteGlove = roundCurrency(propMgbwWhiteGlove + propSuryaGvWhiteGlove);
    const totalFreight = roundCurrency(Math.max(totalShipping - totalWhiteGlove, 0));

    // Fraction of the (line-derived) subtotal attributable to MGBW; used to split cart-level
    // freight/tax when the per-brand properties are not yet available.
    const subtotalBase = mgbwSubtotal + suryaGvSubtotal;
    const mgbwShare = subtotalBase > 0 ? mgbwSubtotal / subtotalBase : 0;

    const allocate = (total: number, propMgbw: number, propSuryaGv: number): { mgbw: number; suryaGv: number } => {
        // Prefer the authoritative per-brand values once the backend has populated them.
        if (propMgbw + propSuryaGv > 0) {
            return { mgbw: roundCurrency(propMgbw), suryaGv: roundCurrency(propSuryaGv) };
        }
        // Otherwise split the cart-level total by subtotal share; suryaGv takes the remainder
        // to avoid rounding drift.
        const mgbwPart = roundCurrency(total * mgbwShare);
        return { mgbw: mgbwPart, suryaGv: roundCurrency(total - mgbwPart) };
    };

    const freightSplit = allocate(totalFreight, propMgbwFreight, propSuryaGvFreight);
    const whiteGloveSplit = allocate(totalWhiteGlove, propMgbwWhiteGlove, propSuryaGvWhiteGlove);
    const taxSplit = allocate(totalTax, propMgbwTax, propSuryaGvTax);

    // Shipping promotions apply only to standard freight. White Glove remains a separate,
    // non-discountable service. MGBW is rounded to cents and Surya/GV receives the remainder
    // so the allocated discount always reconciles.
    const shippingDiscountSplit = allocateShippingDiscount(
        shippingPromotionTotal,
        freightSplit.mgbw,
        freightSplit.suryaGv,
    );

    const mgbw = buildGroup(
        mgbwSubtotal,
        taxSplit.mgbw,
        freightSplit.mgbw,
        whiteGloveSplit.mgbw,
        mgbwRollPadService,
        shippingDiscountSplit.mgbw,
    );
    const suryaGv = buildGroup(
        suryaGvSubtotal,
        taxSplit.suryaGv,
        freightSplit.suryaGv,
        whiteGloveSplit.suryaGv,
        suryaGvRollPadService,
        shippingDiscountSplit.suryaGv,
    );

    const hasMgbw = mgbwLines.length > 0;
    const hasSuryaGv = suryaGvLines.length > 0;
    const hasGlobalViews = suryaGvLines.some(isGlobalViewsLine);
    const hasSurya = suryaGvLines.some(line => !isGlobalViewsLine(line));

    // Prefer the authoritative cart grand total; fall back to the summed group totals.
    const grandTotal = roundCurrency(
        cart?.orderGrandTotal != null ? cart.orderGrandTotal : mgbw.total + suryaGv.total,
    );

    let scenario: PaymentBreakdownScenario;
    let availableChoices: PaymentSplitChoice[];
    let defaultChoice: PaymentSplitChoice;

    // Terms with the MG+BW entity remove the prepay-MGBW constraint entirely (terms-for-all).
    const mgbwTermsApply = userHasTerms && userHasMgbwTerms;

    if (!userHasTerms || (hasMgbw && !hasSuryaGv && !mgbwTermsApply)) {
        // No terms, or an MGBW-only cart for a customer without MGBW terms:
        // the entire order is charged today.
        scenario = "allCreditCard";
        availableChoices = ["prepayAll"];
        defaultChoice = "prepayAll";
    } else if (hasMgbw && hasSuryaGv && !mgbwTermsApply) {
        // MGBW must be prepaid; Surya/GV can go on terms.
        scenario = "mgbwPrepaySplit";
        availableChoices = ["prepayMgbwOnly", "prepayAll"];
        defaultChoice = "prepayMgbwOnly";
    } else {
        // No MGBW in the cart, or the customer has MGBW terms (terms-for-all): the
        // whole order can be placed on terms or paid by card today - no forced split.
        // Terms is the primary path for customers who have it available.
        scenario = "termsEligible";
        availableChoices = ["payAllOnTerms", "prepayAll"];
        defaultChoice = "payAllOnTerms";
    }

    // Offering an option that charges a card the shopper cannot supply dead-ends checkout, so drop
    // the card-based choices whenever a terms-only choice remains to fall back on.
    if (!isCreditCardAvailable(cart)) {
        const cardFreeChoices = availableChoices.filter(choice => choice === "payAllOnTerms");
        availableChoices = cardFreeChoices;
        defaultChoice = "payAllOnTerms";
    }

    // Terms code disables card, but MGBW still has to be prepaid: charging the full order today is
    // not allowed. An MGBW-only cart keeps prepayAll because there is nothing left to put on terms.
    if (isCreditCardDisabledByTerms(cart)) {
        const withoutPrepayAll = availableChoices.filter(choice => choice !== "prepayAll");
        if (withoutPrepayAll.length) {
            availableChoices = withoutPrepayAll;
            if (!availableChoices.includes(defaultChoice)) {
                defaultChoice = availableChoices[0];
            }
        }
    }

    return {
        scenario,
        hasMgbw,
        hasSuryaGv,
        hasSurya,
        hasGlobalViews,
        mgbw,
        suryaGv,
        grandTotal,
        availableChoices,
        defaultChoice,
    };
};

/**
 * Resolves how much is charged today vs placed on terms for a given split choice.
 */
export const getSplitTotals = (model: PaymentBreakdownModel, choice: PaymentSplitChoice): SplitTotals => {
    switch (choice) {
        case "payAllOnTerms":
            return { chargedToday: 0, onTerms: model.grandTotal };
        case "prepayMgbwOnly":
            return { chargedToday: model.mgbw.total, onTerms: model.suryaGv.total };
        case "prepayAll":
        default:
            return { chargedToday: model.grandTotal, onTerms: 0 };
    }
};

/**
 * A separate MGBW statement charge only applies to mixed carts that include a
 * card charge. MGBW-only carts have no separate merchant to distinguish, and
 * all-terms orders have no card charge to disclose.
 */
export const shouldShowSeparateMgbwChargeNotice = (
    model: PaymentBreakdownModel,
    choice: PaymentSplitChoice,
): boolean => model.hasMgbw && model.hasSuryaGv && choice !== "payAllOnTerms";
